const DEFAULT_FILTER_CATEGORIES = [
  {
    type: 'location',
    title: 'Location',
    placeholder: 'All Levels',
    options: []
  },
  {
    type: 'cuisine',
    title: 'Cuisine',
    placeholder: 'All Cuisines',
    options: []
  },
  {
    type: 'dietary_preferences',
    title: 'Dietary Preferences',
    placeholder: 'No Dietary Preference',
    options: []
  }
]

export const getDefaultHeroBanner = () => ({
  enabled: true,
  title: '',
  image: { url: '', alt: '' }
})

export const normalizeHeroBannerSection = (section = {}) => {
  const merged = {
    ...getDefaultHeroBanner(),
    ...(section || {}),
    image: {
      ...getDefaultHeroBanner().image,
      ...(section?.image || {})
    }
  }

  return {
    enabled: merged.enabled !== false,
    title: String(merged.title ?? '').trim(),
    image: {
      url: merged.image?.url || merged.image?.src || '',
      alt: String(merged.image?.alt ?? '').trim()
    }
  }
}

export const getDefaultFeaturedVendorSection = () => ({
  enabled: true,
  title: '',
  type: 'spotlight',
  galleryItems: []
})

export const getDefaultVendorGridSection = () => ({
  enabled: true,
  title: 'MSG Eats',
  load_more_label: 'More Locations',
  no_results_message: 'No results found. Please try different filters.',
  filter_categories: DEFAULT_FILTER_CATEGORIES.map(category => ({ ...category, options: [] })),
  vendors: []
})

export const getDefaultPromotionalVideo = () => ({
  enabled: true,
  video: { provider: 'youtube', url: '', alt: '' },
  description: ''
})

export const getDefaultMediaOnlyCarouselFooter = () => ({
  enabled: false,
  title: null,
  description: null,
  galleryItems: []
})

export const getDefaultVenuePartnerBenefits = () => ({
  enabled: true,
  data_field_id: '',
  title: '',
  partners_flex_modules: []
})

export const getBlankVenuePartnerBenefits = (existingSection = {}) => ({
  enabled: existingSection.enabled !== false,
  data_field_id: '',
  title: '',
  partners_flex_modules: []
})

export const normalizeVenuePartnerBenefitsSection = (section = {}) => {
  const merged = {
    ...getDefaultVenuePartnerBenefits(),
    ...(section || {})
  }

  return {
    ...merged,
    data_field_id: merged.data_field_id ? String(merged.data_field_id) : ''
  }
}

const mapDataFieldFlexModuleToPartnerBenefit = module => ({
  type: module?.type || 'benefit',
  image: {
    src: module?.image?.url || module?.image?.src || '',
    alt: module?.image?.alt || ''
  },
  title: module?.title || '',
  html_description: module?.html_description || module?.description || '',
  cta: {
    label: module?.cta?.label || '',
    url: module?.cta?.url || '',
    target: module?.cta?.target || '_self'
  }
})

/**
 * Build concession venue partner benefits from one selected structured data record,
 * matching Content Ops `dataFields.venue_benefits` selection behavior.
 */
export const buildVenuePartnerBenefitsFromDataField = (dataField, existingSection = {}) => {
  if (!dataField) {
    return getBlankVenuePartnerBenefits(existingSection)
  }

  return {
    enabled: existingSection.enabled !== false,
    data_field_id: dataField._id ? String(dataField._id) : '',
    title: dataField.title || '',
    partners_flex_modules: (dataField.flex_modules || []).map(mapDataFieldFlexModuleToPartnerBenefit)
  }
}

export const pickVenuePartnerBenefitsDataField = (dataFields = [], selectedId = '') => {
  const benefitFields = (dataFields || []).filter(field => field?.type === 'Venue Partner Benefits')
  if (!benefitFields.length) {
    return null
  }
  if (selectedId) {
    return benefitFields.find(field => String(field._id) === String(selectedId)) || null
  }
  return benefitFields.find(field => field.is_default) || benefitFields[0]
}

/** @deprecated Prefer explicit dropdown selection via buildVenuePartnerBenefitsFromDataField */
export const buildVenuePartnerBenefitsFromDataFields = (dataFields = [], existingSection = {}) => {
  const selectedField = pickVenuePartnerBenefitsDataField(dataFields)

  if (!selectedField) {
    return getBlankVenuePartnerBenefits(existingSection)
  }

  return buildVenuePartnerBenefitsFromDataField(selectedField, existingSection)
}

export const getDefaultConcessionDisclaimer = () => ({
  enabled: true,
  title: '',
  html_description: ''
})

export const GENERIC_CONCESSION_VALIDATION_ERROR = 'Please complete all required fields.'

const stripHtml = value =>
  String(value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

export const getConcessionHeroBannerFieldErrors = (heroBanner = {}) => {
  if (heroBanner.enabled === false) {
    return { title: '', image: '' }
  }

  const title = String(heroBanner.title ?? '').trim()
  const imageUrl = String(heroBanner.image?.url || heroBanner.image?.src || '').trim()

  return {
    title: !title ? 'Banner title is required' : '',
    image: !imageUrl ? 'Hero image is required' : ''
  }
}

export const validateConcessionHeroBanner = (heroBanner = {}) => {
  const fieldErrors = getConcessionHeroBannerFieldErrors(heroBanner)

  if (fieldErrors.title || fieldErrors.image) {
    return {
      isValid: false,
      message: GENERIC_CONCESSION_VALIDATION_ERROR
    }
  }

  return { isValid: true }
}

export const getConcessionDisclaimerFieldErrors = (disclaimer = {}) => {
  if (disclaimer.enabled === false) {
    return { title: '', description: '' }
  }

  const title = String(disclaimer.title ?? '').trim()
  const description = stripHtml(disclaimer.html_description)

  return {
    title: !title ? 'Section header is required' : '',
    description: !description ? 'Description is required' : ''
  }
}

export const validateConcessionDisclaimer = (disclaimer = {}) => {
  const fieldErrors = getConcessionDisclaimerFieldErrors(disclaimer)

  if (fieldErrors.title || fieldErrors.description) {
    return {
      isValid: false,
      message: GENERIC_CONCESSION_VALIDATION_ERROR
    }
  }

  return { isValid: true }
}

export const getDefaultFoodAndDrink = () => ({
  enabled: false,
  venue_scope: 'theatres_only',
  title: 'Food & Drink',
  html_description: ''
})

export const getDefaultConcessionPageSections = () => ({
  hero_banner: getDefaultHeroBanner(),
  featured_vendor_section: getDefaultFeaturedVendorSection(),
  vendor_grid_section: getDefaultVendorGridSection(),
  promotional_video: getDefaultPromotionalVideo(),
  media_only_carousel_footer: getDefaultMediaOnlyCarouselFooter(),
  venue_partner_benefits: getDefaultVenuePartnerBenefits(),
  disclaimer: getDefaultConcessionDisclaimer(),
  food_and_drink: getDefaultFoodAndDrink()
})

const mapLegacyBrowseVendors = browseVendors => {
  if (!browseVendors || typeof browseVendors !== 'object') {
    return null
  }

  const filterCategories = [
    browseVendors.location && {
      type: 'location',
      title: browseVendors.location.title || 'Location',
      placeholder: browseVendors.location.placeholder || 'All Levels',
      options: (browseVendors.location.levels || []).map(level => ({
        id: level.key || '',
        label: level.label || '',
        color: level.color?.value || level.color || ''
      }))
    },
    browseVendors.cuisine && {
      type: 'cuisine',
      title: browseVendors.cuisine.title || 'Cuisine',
      placeholder: browseVendors.cuisine.placeholder || 'All Cuisines',
      options: (browseVendors.cuisine.options || []).map(option => ({
        id: option.key || '',
        label: option.label || '',
        color: option.color?.value || option.color || ''
      }))
    },
    browseVendors.dietary && {
      type: 'dietary_preferences',
      title: browseVendors.dietary.title || 'Dietary Preferences',
      placeholder: browseVendors.dietary.placeholder || 'No Dietary Preference',
      options: (browseVendors.dietary.options || []).map(option => ({
        id: option.key || '',
        label: option.label || '',
        color: option.color?.value || option.color || ''
      }))
    }
  ].filter(Boolean)

  return {
    enabled: browseVendors.is_active !== false,
    title: browseVendors.title || 'MSG Eats',
    load_more_label: 'More Locations',
    no_results_message: 'No results found. Please try different filters.',
    filter_categories: filterCategories.length ? filterCategories : DEFAULT_FILTER_CATEGORIES,
    vendors: Array.isArray(browseVendors.vendors) ? browseVendors.vendors : []
  }
}

export const mergeConcessionPageSections = (page = {}) => {
  const defaults = getDefaultConcessionPageSections()
  const legacyVendorGrid = mapLegacyBrowseVendors(page.browse_vendors)

  return {
    hero_banner: normalizeHeroBannerSection(page.hero_banner),
    featured_vendor_section: {
      ...defaults.featured_vendor_section,
      ...(page.featured_vendor_section || {})
    },
    vendor_grid_section: {
      ...defaults.vendor_grid_section,
      ...(legacyVendorGrid || page.vendor_grid_section || {})
    },
    promotional_video: { ...defaults.promotional_video, ...(page.promotional_video || {}) },
    media_only_carousel_footer: {
      ...defaults.media_only_carousel_footer,
      ...(page.media_only_carousel_footer || {})
    },
    venue_partner_benefits: normalizeVenuePartnerBenefitsSection(page.venue_partner_benefits),
    disclaimer: { ...defaults.disclaimer, ...(page.disclaimer || {}) },
    food_and_drink: { ...defaults.food_and_drink, ...(page.food_and_drink || {}) }
  }
}

export const updateConcessionSection = (page, sectionKey, partial) => ({
  ...page,
  [sectionKey]: {
    ...(page?.[sectionKey] || getDefaultConcessionPageSections()[sectionKey]),
    ...partial
  }
})

/** @deprecated Use getDefaultVendorGridSection */
export const getDefaultBrowseVendors = () => ({
  is_active: true,
  title: 'Food & Drinks',
  location: {
    key: 'location',
    title: 'Location',
    placeholder: 'All Levels',
    levels: []
  },
  cuisine: {
    key: 'cuisine',
    title: 'Cuisine',
    placeholder: 'All Cuisines',
    options: []
  },
  dietary: {
    key: 'dietary',
    title: 'Dietary Preferences',
    placeholder: 'No Dietary Preferences',
    options: []
  },
  vendors: []
})

/** @deprecated Use mergeConcessionPageSections */
export const mergeBrowseVendors = browseVendors => ({
  ...getDefaultBrowseVendors(),
  ...browseVendors
})
